/* Cruscotto Presa Visione — dashboard aggregata dei link pubblici Privacy.
 * Mostra in tempo reale quanti docenti/esterni hanno aperto il link e
 * completato la presa visione. Utile per audit GDPR.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const STATO_META = {
  COMPLETATO:     { label: "✅ Completato",     color: "#1b5e20", bg: "#e8f5e9" },
  IN_CORSO:       { label: "🟠 In corso",       color: "#ef6c00", bg: "#fff3e0" },
  APERTO_NO_PV:   { label: "👁 Aperto, nessuna PV", color: "#1565c0", bg: "#e3f2fd" },
  MAI_APERTO:     { label: "⏳ Mai aperto",     color: "#c62828", bg: "#ffebee" },
  TUTTI_INATTIVI: { label: "🔒 Link inattivi",   color: "#555",    bg: "#f5f5f5" },
};

const AMBITO_META = {
  docente:     { label: "Docente",     icon: "👤", color: "#6a1b9a" },
  trattamento: { label: "Trattamento", icon: "📋", color: "#1565c0" },
  esterno:     { label: "Esterno",     icon: "🏢", color: "#00838f" },
};

export default function PrivacyFascicoloDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filtroAmbito, setFiltroAmbito] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showAlertConfig, setShowAlertConfig] = useState(false);

  const carica = async () => {
    setErr(null);
    try {
      const r = await api.get(`/privacy/fascicolo/stats/dashboard/presa-visione`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento cruscotto");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(carica, 15000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const righe = data?.righe || [];
  const summary = data?.summary || {};
  const ambiti = data?.ambiti || {};

  const filteredRighe = useMemo(() => {
    return righe.filter(r => {
      if (filtroAmbito && r.ambito !== filtroAmbito) return false;
      if (filtroStato && r.stato_aggregato !== filtroStato) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.label || "").toLowerCase().includes(s) &&
            !(r.codice_entita || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [righe, filtroAmbito, filtroStato, search]);

  const goToFascicolo = (r) => navigate(`/g/admin/privacy/fascicolo/${r.ambito}/${encodeURIComponent(r.codice_entita)}`);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT") : "—";

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <h2 style={{ color: "#37474f", fontSize: 20, margin: 0 }} data-testid="dashboard-title">
          📊 Cruscotto Presa Visione
        </h2>
        <span style={{ fontSize: 12, color: "#666" }}>
          Stato dei link pubblici dei fascicoli privacy · Aggiornamento in tempo reale
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555", cursor: "pointer" }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)}
              data-testid="dashboard-autorefresh" />
            Auto refresh (15s)
          </label>
          <button onClick={carica} data-testid="dashboard-refresh"
            style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            🔄 Aggiorna
          </button>
          <button onClick={() => setShowAlertConfig(true)} data-testid="dashboard-open-alerts"
            style={{ padding: "6px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            ✉ Alert email
          </button>
        </div>
      </div>

      {showAlertConfig && <AlertConfigModal onClose={() => setShowAlertConfig(false)} /> }

      {err && (
        <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, marginBottom: 12, border: "1px solid #ef9a9a" }}>
          {err}
        </div>
      )}

      {loading && <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>}

      {!loading && data && (
        <>
          {/* Stat cards macro */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
            <BigStat icon="🔗" label="Link totali" value={summary.total_links} color="#37474f" sub={`${summary.attivi} attivi · ${summary.revocati} revocati`} />
            <BigStat icon="👤" label="Entità coinvolte" value={summary.tot_entita} color="#1565c0" sub="docenti/trattamenti/esterni" />
            <BigStat icon="👁" label="Link aperti" value={summary.aperti} color="#ef6c00"
              sub={summary.total_links ? `${Math.round(100*summary.aperti/summary.total_links)}% dei link` : ""} />
            <BigStat icon="✅" label="Documenti visti" value={`${summary.tot_docs_visti}/${summary.tot_docs_attesi}`}
              color="#2e7d32" sub={`${summary.tot_docs_pct}% completamento`} progressPct={summary.tot_docs_pct} />
            <BigStat icon="⏳" label="Link mai aperti" value={summary.mai_aperti} color="#c62828"
              sub={summary.total_links ? `${Math.round(100*(summary.mai_aperti||0)/summary.total_links)}% dei link` : ""} />
          </div>

          {/* Breakdown per ambito */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 18 }}>
            {["docente", "trattamento", "esterno"].map(a => {
              const s = ambiti[a] || {};
              const meta = AMBITO_META[a];
              return (
                <div key={a} data-testid={`dashboard-ambito-${a}`}
                  style={{ background: "white", border: `2px solid ${meta.color}`, borderRadius: 6, padding: 14, cursor: "pointer" }}
                  onClick={() => setFiltroAmbito(filtroAmbito === a ? "" : a)}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: meta.color, marginBottom: 8 }}>
                    {meta.icon} {meta.label}
                    {filtroAmbito === a && <span style={{ marginLeft: 6, fontSize: 10, background: meta.color, color: "white", padding: "1px 6px", borderRadius: 8 }}>FILTRO ATTIVO</span>}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#37474f" }}>
                    {s.tot_docs_visti || 0}<span style={{ fontSize: 14, color: "#888", fontWeight: 400 }}>/{s.tot_docs_attesi || 0}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>documenti visti · <b>{s.tot_docs_pct || 0}%</b></div>
                  <ProgressBar pct={s.tot_docs_pct || 0} color={meta.color} />
                  <div style={{ fontSize: 11, color: "#555", marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <span><b>{s.entita || 0}</b> entità</span>
                    <span>•</span>
                    <span><b>{s.links || 0}</b> link</span>
                    <span>•</span>
                    <span style={{ color: "#1b5e20" }}>✓ {s.completati || 0}</span>
                    <span style={{ color: "#ef6c00" }}>● {s.in_corso || 0}</span>
                    <span style={{ color: "#c62828" }}>⏳ {s.mai_aperti || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filtri */}
          <div style={{ background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 0, borderBottom: "none" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>FILTRI:</span>
            <input type="text" placeholder="🔍 Cerca per nome/codice..."
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="dashboard-search"
              style={{ padding: "5px 10px", border: "1px solid #aaa", fontSize: 12, width: 250 }} />
            <select value={filtroAmbito} onChange={e => setFiltroAmbito(e.target.value)}
              data-testid="dashboard-filter-ambito"
              style={{ padding: "5px 10px", border: "1px solid #aaa", fontSize: 12 }}>
              <option value="">Tutti gli ambiti</option>
              <option value="docente">👤 Docente</option>
              <option value="trattamento">📋 Trattamento</option>
              <option value="esterno">🏢 Esterno</option>
            </select>
            <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)}
              data-testid="dashboard-filter-stato"
              style={{ padding: "5px 10px", border: "1px solid #aaa", fontSize: 12 }}>
              <option value="">Tutti gli stati</option>
              {Object.entries(STATO_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            {(filtroAmbito || filtroStato || search) && (
              <button onClick={() => { setFiltroAmbito(""); setFiltroStato(""); setSearch(""); }}
                style={{ padding: "5px 12px", background: "#fff3e0", color: "#bf360c", border: "1px solid #ffb74d", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                ✕ Pulisci filtri
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
              <b>{filteredRighe.length}</b> / {righe.length} entità
            </span>
          </div>

          {/* Tabella dettaglio */}
          <div style={{ background: "white", border: "1px solid #ccc" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead className="thead-blue">
                <tr>
                  <th style={{ width: 100, padding: 6, textAlign: "left" }}>AMBITO</th>
                  <th style={{ padding: 6, textAlign: "left" }}>ENTITÀ</th>
                  <th style={{ width: 80, padding: 6, textAlign: "center" }}>LINK</th>
                  <th style={{ width: 80, padding: 6, textAlign: "center" }}>VISTE</th>
                  <th style={{ width: 130, padding: 6, textAlign: "center" }}>PRESA VISIONE</th>
                  <th style={{ width: 150, padding: 6, textAlign: "center" }}>STATO</th>
                  <th style={{ width: 140, padding: 6, textAlign: "left" }}>ULTIMO ACCESSO</th>
                  <th style={{ width: 80, padding: 6, textAlign: "center" }}>AZIONI</th>
                </tr>
              </thead>
              <tbody>
                {filteredRighe.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                    {righe.length === 0 ? "Nessun link pubblico generato. Vai su un fascicolo e crea un link." : "Nessuna entità corrisponde ai filtri."}
                  </td></tr>
                )}
                {filteredRighe.map((r, i) => {
                  const amb = AMBITO_META[r.ambito] || { label: r.ambito, icon: "❓", color: "#888" };
                  const st = STATO_META[r.stato_aggregato] || { label: r.stato_aggregato, color: "#888", bg: "#eee" };
                  return (
                    <tr key={`${r.ambito}-${r.codice_entita}`} style={{ borderTop: "1px solid #eee" }}
                      data-testid={`dashboard-row-${i}`}>
                      <td style={{ padding: 6, color: amb.color, fontWeight: 700 }}>
                        {amb.icon} {amb.label}
                      </td>
                      <td style={{ padding: 6 }}>
                        <div style={{ fontWeight: 600 }}>{r.label}</div>
                        <div style={{ fontSize: 10, color: "#888", fontFamily: "Consolas, monospace" }}>{r.codice_entita}</div>
                      </td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{r.links_count}</div>
                        <div style={{ fontSize: 9, color: "#666" }}>
                          <span style={{ color: "#2e7d32" }}>✓{r.links_attivi}</span>
                          {r.links_scaduti > 0 && <> · <span style={{ color: "#ef6c00" }}>⏰{r.links_scaduti}</span></>}
                          {r.links_revocati > 0 && <> · <span style={{ color: "#c62828" }}>✕{r.links_revocati}</span></>}
                        </div>
                      </td>
                      <td style={{ padding: 6, textAlign: "center", fontSize: 14, fontWeight: 700,
                        color: r.view_count_totale > 0 ? "#1565c0" : "#aaa" }}>
                        {r.view_count_totale}
                      </td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <div style={{ fontWeight: 700, color: r.pv_pct >= 100 ? "#1b5e20" : r.pv_count > 0 ? "#ef6c00" : "#888" }}>
                          {r.pv_count}/{r.pv_attesi} <span style={{ fontSize: 10, fontWeight: 400 }}>({r.pv_pct}%)</span>
                        </div>
                        <ProgressBar pct={r.pv_pct} color={r.pv_pct >= 100 ? "#2e7d32" : "#ef6c00"} small />
                      </td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <span style={{ background: st.bg, color: st.color, padding: "3px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10, whiteSpace: "nowrap" }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: 6, fontSize: 11, color: "#666" }}>
                        {fmtDate(r.last_viewed_at)}
                      </td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <button onClick={() => goToFascicolo(r)}
                          data-testid={`dashboard-open-${i}`}
                          style={{ background: "#ef6c00", color: "white", border: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          📁 Apri
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 14, fontSize: 10, color: "#888", textAlign: "right" }}>
            Auto refresh {autoRefresh ? "attivo (15s)" : "disattivato"} · Click sulle card ambito per filtrare
          </div>
        </>
      )}
    </div>
  );
}

/* ============== Componenti UI ============== */

function BigStat({ icon, label, value, color, sub, progressPct }) {
  return (
    <div style={{ background: "white", borderRadius: 6, padding: 14, borderLeft: `4px solid ${color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>{icon} {label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, marginTop: 4 }}>{value ?? 0}</div>
      {sub && <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>{sub}</div>}
      {progressPct !== undefined && <ProgressBar pct={progressPct} color={color} />}
    </div>
  );
}

function ProgressBar({ pct, color, small }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{ marginTop: small ? 3 : 6, height: small ? 4 : 6, background: "#e0e0e0", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${p}%`, height: "100%", background: color, transition: "width 0.4s ease" }} />
    </div>
  );
}


/* ============== Modal Configurazione Alert Email ============== */

function AlertConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [nextRun, setNextRun] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [recipientInput, setRecipientInput] = useState("");

  const carica = async () => {
    setLoading(true);
    try {
      const [r, nr] = await Promise.all([
        api.get("/privacy/alerts/config"),
        api.get("/privacy/alerts/next-run"),
      ]);
      setCfg(r.data);
      setNextRun(nr.data?.next_run || null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento configurazione");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carica(); }, []);

  const update = (patch) => setCfg(c => ({ ...c, ...patch }));

  const addRecipient = () => {
    const e = (recipientInput || "").trim();
    if (!e || !e.includes("@")) { setErr("Email non valida"); return; }
    if ((cfg.recipients || []).includes(e)) { setErr("Email già presente"); return; }
    update({ recipients: [...(cfg.recipients || []), e] });
    setRecipientInput("");
    setErr(null);
  };
  const removeRecipient = (em) => update({ recipients: (cfg.recipients || []).filter(x => x !== em) });

  const salva = async () => {
    setSaving(true); setErr(null); setMsg(null);
    try {
      const r = await api.put("/privacy/alerts/config", cfg);
      setCfg(r.data);
      const nr = await api.get("/privacy/alerts/next-run");
      setNextRun(nr.data?.next_run || null);
      setMsg("✅ Configurazione salvata.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const inviaTest = async () => {
    if (!(cfg.recipients || []).length) { setErr("Aggiungi almeno un destinatario"); return; }
    if (!window.confirm(`Inviare ora un'email di prova ai ${cfg.recipients.length} destinatari configurati?`)) return;
    setTesting(true); setErr(null); setMsg(null);
    try {
      const r = await api.post("/privacy/alerts/send-test");
      if (r.data?.sent) {
        setMsg(`✅ Email inviata a ${(r.data.recipients || []).length} destinatari. Oggetto: "${r.data.subject || ''}"`);
      } else {
        setErr(`Email non inviata: ${r.data?.reason || "errore"}${r.data?.error ? " · " + r.data.error : ""}`);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore invio");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div onClick={onClose} data-testid="alert-config-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 30, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 720, marginTop: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.35)", borderRadius: 4 }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "12px 18px", display: "flex", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>✉ Alert email automatico — Presa Visione</div>
          <button onClick={onClose} data-testid="alert-close"
            style={{ marginLeft: "auto", background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
        {loading && <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>}
        {!loading && cfg && (
          <div style={{ padding: 18 }}>
            {err && <div style={{ background: "#ffebee", color: "#c62828", padding: 10, marginBottom: 12, fontSize: 12, border: "1px solid #ef9a9a" }}>{err}</div>}
            {msg && <div style={{ background: "#e8f5e9", color: "#1b5e20", padding: 10, marginBottom: 12, fontSize: 12, border: "1px solid #a5d6a7" }}>{msg}</div>}

            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 12, background: cfg.enabled ? "#e8f5e9" : "#fafafa", border: `1px solid ${cfg.enabled ? "#a5d6a7" : "#ddd"}`, borderRadius: 4, marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", color: cfg.enabled ? "#1b5e20" : "#666" }}>
                <input type="checkbox" checked={!!cfg.enabled} onChange={e => update({ enabled: e.target.checked })}
                  data-testid="alert-enabled" style={{ width: 18, height: 18 }} />
                {cfg.enabled ? "✅ Alert attivo" : "Alert disattivato"}
              </label>
              {cfg.enabled && nextRun && (
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#1b5e20" }}>
                  ⏰ Prossima esecuzione: <b>{new Date(nextRun).toLocaleString("it-IT")}</b>
                </span>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>👥 Destinatari email</div>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input type="email" value={recipientInput} onChange={e => setRecipientInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addRecipient()}
                  placeholder="nome@esempio.it"
                  data-testid="alert-recipient-input"
                  style={{ flex: 1, padding: "7px 10px", border: "1px solid #aaa", fontSize: 13 }} />
                <button onClick={addRecipient} data-testid="alert-recipient-add"
                  style={{ padding: "7px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  + Aggiungi
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(cfg.recipients || []).length === 0 && (
                  <span style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>Nessun destinatario configurato</span>
                )}
                {(cfg.recipients || []).map((e, i) => (
                  <span key={e} data-testid={`alert-recipient-${i}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e3f2fd", color: "#0d47a1", padding: "4px 10px", borderRadius: 14, fontSize: 12, fontWeight: 600 }}>
                    {e}
                    <button onClick={() => removeRecipient(e)} title="Rimuovi"
                      style={{ background: "none", border: "none", color: "#c62828", cursor: "pointer", fontWeight: 700, fontSize: 13, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 4 }}>📅 Giorni minimi dall'invio del link</div>
                <input type="number" min="1" max="365" value={cfg.giorni_minimi}
                  onChange={e => update({ giorni_minimi: parseInt(e.target.value || 30, 10) })}
                  data-testid="alert-giorni"
                  style={{ width: "100%", padding: "7px 10px", border: "1px solid #aaa", fontSize: 13 }} />
                <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>Vengono valutati solo i link più vecchi di N giorni</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 4 }}>🎯 % minima di presa visione</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="number" min="0" max="100" value={cfg.pct_minima}
                    onChange={e => update({ pct_minima: parseInt(e.target.value || 0, 10) })}
                    data-testid="alert-pct"
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #aaa", fontSize: 13 }} />
                  <span style={{ fontSize: 13, color: "#555" }}>%</span>
                </div>
                <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>Sotto questa soglia il fascicolo è "in allarme"</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: cfg.frequenza === "weekly" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 4 }}>🔁 Frequenza</div>
                <select value={cfg.frequenza} onChange={e => update({ frequenza: e.target.value })}
                  data-testid="alert-frequenza"
                  style={{ width: "100%", padding: "7px 10px", border: "1px solid #aaa", fontSize: 13 }}>
                  <option value="daily">Giornaliero</option>
                  <option value="weekly">Settimanale</option>
                </select>
              </div>
              {cfg.frequenza === "weekly" && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 4 }}>📆 Giorno</div>
                  <select value={cfg.weekday} onChange={e => update({ weekday: e.target.value })}
                    data-testid="alert-weekday"
                    style={{ width: "100%", padding: "7px 10px", border: "1px solid #aaa", fontSize: 13 }}>
                    <option value="mon">Lunedì</option>
                    <option value="tue">Martedì</option>
                    <option value="wed">Mercoledì</option>
                    <option value="thu">Giovedì</option>
                    <option value="fri">Venerdì</option>
                    <option value="sat">Sabato</option>
                    <option value="sun">Domenica</option>
                  </select>
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 4 }}>🕗 Orario (Europe/Rome)</div>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <input type="number" min="0" max="23" value={cfg.hour}
                    onChange={e => update({ hour: parseInt(e.target.value || 0, 10) })}
                    data-testid="alert-hour"
                    style={{ width: 60, padding: "7px 10px", border: "1px solid #aaa", fontSize: 13, textAlign: "center" }} />
                  <span>:</span>
                  <input type="number" min="0" max="59" value={cfg.minute}
                    onChange={e => update({ minute: parseInt(e.target.value || 0, 10) })}
                    data-testid="alert-minute"
                    style={{ width: 60, padding: "7px 10px", border: "1px solid #aaa", fontSize: 13, textAlign: "center" }} />
                </div>
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555", cursor: "pointer", marginBottom: 14 }}>
              <input type="checkbox" checked={!!cfg.send_when_ok} onChange={e => update({ send_when_ok: e.target.checked })}
                data-testid="alert-send-when-ok" />
              Invia email anche quando tutto è OK (report "✅ Presa visione OK")
            </label>

            {cfg.last_run_at && (
              <div style={{ background: "#f5f5f5", padding: 10, fontSize: 11, color: "#555", borderRadius: 3, marginBottom: 14 }}>
                <b>Ultimo invio:</b> {new Date(cfg.last_run_at).toLocaleString("it-IT")} · stato: {cfg.last_status}
                {cfg.last_summary && <> · {cfg.last_summary.tot_sotto_soglia} sotto soglia / {cfg.last_summary.tot_entita_valutate} totali</>}
                {cfg.last_error && <div style={{ color: "#c62828", marginTop: 4 }}>Errore: {cfg.last_error}</div>}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #eee", paddingTop: 12 }}>
              <button onClick={inviaTest} disabled={testing || saving}
                data-testid="alert-send-test"
                style={{ padding: "8px 14px", background: "white", color: "#ef6c00", border: "1px solid #ef6c00", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: testing ? 0.5 : 1 }}>
                {testing ? "⏳ Invio..." : "📧 Invia email di prova ora"}
              </button>
              <button onClick={onClose}
                style={{ padding: "8px 14px", background: "white", color: "#555", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Chiudi
              </button>
              <button onClick={salva} disabled={saving}
                data-testid="alert-save"
                style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>
                {saving ? "⏳ Salvataggio..." : "💾 Salva configurazione"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
